 
import { Dispatch } from 'redux'
import { api, apiErrorHandler, getApiRequestConfig, Response } from '../../api'
import { GetState } from '../reducers'
import { AdminPlan, AuthAdmin } from '../reducers/authAdmin.reducer'
import { BankDetails, DeliveryAPI, DeliveryCharge, DeliveryDistance, LateNightDelivery, OrderValueForFreeDelivery, OtherPayments, Paytrail, Pickup, PreviewTheme, ShippingDelivery, Shop, ShopAddress, ShopCategory, Theme, Timing } from '../reducers/shop.reducer'
import { AddShopAction, ShopActionTypes } from './shop.actions'

export enum AuthAdminActionTypes {
    LOGIN_ADMIN = 'LOGIN_ADMIN',
    LOGOUT_ADMIN = 'LOGOUT_ADMIN',
    UPDATE_SHOP_CONTACT_DETAILS = 'UPDATE_SHOP_CONTACT_DETAILS',
    UPDATE_SHOP_BANK_DETAILS = 'UPDATE_SHOP_BANK_DETAILS',
    UPDATE_SHOP_OTHER_PAYMENTS = 'UPDATE_SHOP_OTHER_PAYMENTS',
    UPDATE_SHOP_TIMING = 'UPDATE_SHOP_TIMING',
    UPDATE_DELIVERY_TIMING = 'UPDATE_DELIVERY_TIMING',
    ENABLE_DELIVERY = 'ENABLE_DELIVERY',
    DISABLE_DELIVERY = 'DISABLE_DELIVERY',
    ENABLE_SCHEDULED_ORDER = 'ENABLE_SCHEDULED_ORDER',
    DISABLE_SCHEDULED_ORDER = 'DISABLE_SCHEDULED_ORDER',
    UPDATE_DELIVERY_CHARGE_AND_DISTANCE = 'UPDATE_DELIVERY_CHARGE_AND_DISTANCE',
    UPDATE_DELIVERY_API = 'UPDATE_DELIVERY_API',
    UPDATE_PAYTRAIL = 'UPDATE_PAYTRAIL',
    UPDATE_PROCESS_TIMES = 'UPDATE_PROCESS_TIMES',
    UPDATE_SHOP_THEME = 'UPDATE_SHOP_THEME',
    UPDATE_PICKUP = 'UPDATE_PICKUP',
    UPDATE_SHOP_PREVIEW_THEME = 'UPDATE_SHOP_PREVIEW_THEME',
    UPDATE_SHOP_VAT_ID = 'UPDATE_SHOP_VAT_ID',
    UPDATE_SHOP_ANNOUNCEMENT = 'UPDATE_SHOP_ANNOUNCEMENT',
    UPDATE_SHOP_ORDERS_PAUSE = 'UPDATE_SHOP_ORDERS_PAUSE',
    UPDATE_SHIPPING_DELIVERY = 'UPDATE_SHIPPING_DELIVERY'
}

export type AdminCategory = ShopCategory

export type AdminRole = 'admin' | 'chef'

export interface LoginAdminAction {
    type: AuthAdminActionTypes.LOGIN_ADMIN
    data: AuthAdmin
}

export interface LogoutAdminAction {
    type: AuthAdminActionTypes.LOGOUT_ADMIN
}

export interface UpdateShopContactDetailsAction {
    type: AuthAdminActionTypes.UPDATE_SHOP_CONTACT_DETAILS
    data: UpdateShopContactDetailsResponse
}

export interface UpdateShopBankDetailsAction {
    type: AuthAdminActionTypes.UPDATE_SHOP_BANK_DETAILS
    data: BankDetails
}

export interface UpdateShopOtherPaymentsAction {
    type: AuthAdminActionTypes.UPDATE_SHOP_OTHER_PAYMENTS
    data: OtherPayments[]
}

export interface UpdateShopTimingAction {
    type: AuthAdminActionTypes.UPDATE_SHOP_TIMING
    data: Timing
}

export interface UpdateDeliveryTimingAction {
    type: AuthAdminActionTypes.UPDATE_DELIVERY_TIMING
    data: Timing
}

export interface EnableDeliveryAction {
    type: AuthAdminActionTypes.ENABLE_DELIVERY
}

export interface DisableDeliveryAction {
    type: AuthAdminActionTypes.DISABLE_DELIVERY
}

export interface EnableScheduledOrderAction {
    type: AuthAdminActionTypes.ENABLE_SCHEDULED_ORDER
}

export interface DisableScheduledOrderAction {
    type: AuthAdminActionTypes.DISABLE_SCHEDULED_ORDER
}

export interface UpdateDeliveryChargeAndDistanceAction {
    type: AuthAdminActionTypes.UPDATE_DELIVERY_CHARGE_AND_DISTANCE
    data: UpdateDeliveryChargeAndDistanceResponse
}

export interface UpdateDeliveryAPIAction {
    type: AuthAdminActionTypes.UPDATE_DELIVERY_API
    data: DeliveryAPI
}

export interface UpdatePaytrailAction {
    type: AuthAdminActionTypes.UPDATE_PAYTRAIL
    data: Paytrail
}

export interface UpdateProcessTimesAction {
    type: AuthAdminActionTypes.UPDATE_PROCESS_TIMES
    data: number[]
}

export interface UpdateShopThemeAction {
    type: AuthAdminActionTypes.UPDATE_SHOP_THEME
    data: Theme
}

export interface UpdatePickupAction {
    type: AuthAdminActionTypes.UPDATE_PICKUP
    data: Pickup
}

export interface UpdateShopPreviewThemeAction {
    type: AuthAdminActionTypes.UPDATE_SHOP_PREVIEW_THEME
    data: PreviewTheme
}

export interface UpdateShopVatIdAction {
    type: AuthAdminActionTypes.UPDATE_SHOP_VAT_ID
    data: string | null
}

export interface UpdateShopAnnouncementAction {
    type: AuthAdminActionTypes.UPDATE_SHOP_ANNOUNCEMENT
    data: string | null
}

export interface UpdateShopOrdersPauseAction {
    type: AuthAdminActionTypes.UPDATE_SHOP_ORDERS_PAUSE
    data: { ordersPausedUntil: string | null }
}

export interface UpdateShippingDeliveryAction {
    type: AuthAdminActionTypes.UPDATE_SHIPPING_DELIVERY
    data: Partial<ShippingDelivery>
}

export type AuthAdminActions = UpdateShippingDeliveryAction | LoginAdminAction | LogoutAdminAction | UpdateShopContactDetailsAction | UpdateShopBankDetailsAction | UpdateShopOtherPaymentsAction | UpdateShopTimingAction | UpdateDeliveryTimingAction | EnableDeliveryAction | DisableDeliveryAction | EnableScheduledOrderAction | DisableScheduledOrderAction | UpdateDeliveryChargeAndDistanceAction | UpdateDeliveryAPIAction | UpdatePaytrailAction | UpdateProcessTimesAction | UpdateShopThemeAction | UpdateShopPreviewThemeAction | UpdateShopVatIdAction | UpdateShopAnnouncementAction | UpdateShopOrdersPauseAction | UpdatePickupAction

interface RegisterAdmin {
    name: string
    username: string
    divisionId?: string
    category: AdminCategory
    email: string
    password: string
    shopName: string
    businessName: string
    websiteUrl: string
    addressLine1: string
    addressLine2: string
    city: string
    pincode: string
    state: string
    landmark: string | null
    currency: string | null
    description: string
    plan?: AdminPlan
}

interface RegisterAdminResponse {
    name: string
    email: string
    username: string
    category: AdminCategory
    role: AdminRole
    shop: Shop
    createdAt: string
    updatedAt: string
    id: string
}

export const registerAdmin = (input: RegisterAdmin, updatedState?: boolean) => async (dispatch: Dispatch) => {
    return api.post<Response<RegisterAdminResponse>>('admin', input).then(response => {
        if (response.status === 200) {
            if (updatedState) {
                dispatch<AddShopAction>({
                    type: ShopActionTypes.ADD_SHOP,
                    data: response.data.data.shop
                })
            }
            return Promise.resolve<string>(response.data.message || 'Registered')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Failed to register'))
    })
}


export const registerShop = (input: RegisterAdmin, updatedState?: boolean) => async (dispatch: Dispatch) => {
    return api.post<Response<any>>('shop', input).then(response => {
        if (response.status === 200) {
            return Promise.resolve<string>(response.data.message || 'Registered successfully, Vshop Team contact you soon!')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Failed to register'))
    })
}

interface UpdateShopContactDetails {
    shopName: string
    businessName: string | null
    addressLine1: string
    addressLine2: string
    city: string
    pincode: string
    state: string
    landmark: string | null
    countryCode: string | null
    phoneNumber: string | null
    currency: string
    description: string
    plan?: AdminPlan
}

interface UpdateShopContactDetailsResponse {
    shopName: string
    businessName: string | null
    countryCode: string | null
    currency: string
    phoneNumber: string | null
    description: string
    address: ShopAddress
}

export const updateShopContactDetails = (input: UpdateShopContactDetails) => async (dispatch: Dispatch, getState: GetState) => {
    const authAdmin = getState().authAdmin
    const config = getApiRequestConfig(authAdmin?.['auth-token'])

    return api.put<Response<UpdateShopContactDetailsResponse>>('shop/contact-details', input, config).then(response => {
        if (response.status === 200) {
            dispatch<UpdateShopContactDetailsAction>({
                type: AuthAdminActionTypes.UPDATE_SHOP_CONTACT_DETAILS,
                data: response.data.data
            })
            return Promise.resolve<string>(response.data.message || 'Updated')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to update'))
    })
}

export const updateShopBankDetails = (input: BankDetails) => async (dispatch: Dispatch, getState: GetState) => {
    const authAdmin = getState().authAdmin
    const config = getApiRequestConfig(authAdmin?.['auth-token'])

    return api.put<Response<BankDetails>>('shop/bank-details', input, config).then(response => {
        if (response.status === 200) {
            dispatch<UpdateShopBankDetailsAction>({
                type: AuthAdminActionTypes.UPDATE_SHOP_BANK_DETAILS,
                data: response.data.data
            })
            return Promise.resolve<string>(response.data.message || 'Updated')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to update'))
    })
}

export const updateShopOtherPayments = (input: OtherPayments[]) => async (dispatch: Dispatch, getState: GetState) => {
    const authAdmin = getState().authAdmin
    const config = getApiRequestConfig(authAdmin?.['auth-token'])

    return api.put<Response<{ otherPayments: OtherPayments[] }>>('shop/other-payments', { otherPayments: input }, config).then(response => {
        if (response.status === 200) {
            dispatch<UpdateShopOtherPaymentsAction>({
                type: AuthAdminActionTypes.UPDATE_SHOP_OTHER_PAYMENTS,
                data: response.data.data?.otherPayments ?? []
            })
            return Promise.resolve<string>(response.data.message || 'Updated')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to update'))
    })
}

export const updateShopTiming = (input: Timing) => async (dispatch: Dispatch, getState: GetState) => {
    const authAdmin = getState().authAdmin
    const config = getApiRequestConfig(authAdmin?.['auth-token'])

    return api.put<Response<Timing>>('shop/timing', input, config).then(response => {
        if (response.status === 200) {
            dispatch<UpdateShopTimingAction>({
                type: AuthAdminActionTypes.UPDATE_SHOP_TIMING,
                data: response.data.data
            })
            return Promise.resolve<string>(response.data.message || 'Updated')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to update'))
    })
}

export const updateDeliveryTiming = (input: Timing) => async (dispatch: Dispatch, getState: GetState) => {
    const authAdmin = getState().authAdmin
    const config = getApiRequestConfig(authAdmin?.['auth-token'])

    return api.put<Response<Timing>>('shop/delivery-timing', input, config).then(response => {
        if (response.status === 200) {
            dispatch<UpdateDeliveryTimingAction>({
                type: AuthAdminActionTypes.UPDATE_DELIVERY_TIMING,
                data: response.data.data
            })
            return Promise.resolve<string>(response.data.message || 'Updated')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to update'))
    })
}

export const enableDelivery = () => async (dispatch: Dispatch, getState: GetState) => {
    const authAdmin = getState().authAdmin
    const config = getApiRequestConfig(authAdmin?.['auth-token'])

    return api.put('shop/delivery/enable', {}, config).then(response => {
        if (response.status === 200) {
            dispatch<EnableDeliveryAction>({
                type: AuthAdminActionTypes.ENABLE_DELIVERY
            })
            return Promise.resolve<string>(response.data.message || 'Delivery enabled')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to update'))
    })
}

export const disableDelivery = () => async (dispatch: Dispatch, getState: GetState) => {
    const authAdmin = getState().authAdmin
    const config = getApiRequestConfig(authAdmin?.['auth-token'])

    return api.put('shop/delivery/disable', {}, config).then(response => {
        if (response.status === 200) {
            dispatch<DisableDeliveryAction>({
                type: AuthAdminActionTypes.DISABLE_DELIVERY
            })
            return Promise.resolve<string>(response.data.message || 'Delivery disabled')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to update'))
    })
}

export const enableScheduledOrder = () => async (dispatch: Dispatch, getState: GetState) => {
    const authAdmin = getState().authAdmin
    const config = getApiRequestConfig(authAdmin?.['auth-token'])

    return api.put('shop/scheduled-order/enable', {}, config).then(response => {
        if (response.status === 200) {
            dispatch<EnableScheduledOrderAction>({
                type: AuthAdminActionTypes.ENABLE_SCHEDULED_ORDER
            })
            if (authAdmin?.shop?.id) {
                dispatch({
                    type: ShopActionTypes.UPDATE_SHOP_SCHEDULED_ORDER,
                    shopId: authAdmin.shop.id,
                    data: { scheduledOrderEnabled: true }
                })
            }
            return Promise.resolve<string>(response.data.message || 'Scheduled order enabled')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to update'))
    })
}

export const disableScheduledOrder = () => async (dispatch: Dispatch, getState: GetState) => {
    const authAdmin = getState().authAdmin
    const config = getApiRequestConfig(authAdmin?.['auth-token'])

    return api.put('shop/scheduled-order/disable', {}, config).then(response => {
        if (response.status === 200) {
            dispatch<DisableScheduledOrderAction>({
                type: AuthAdminActionTypes.DISABLE_SCHEDULED_ORDER
            })
            if (authAdmin?.shop?.id) {
                dispatch({
                    type: ShopActionTypes.UPDATE_SHOP_SCHEDULED_ORDER,
                    shopId: authAdmin.shop.id,
                    data: { scheduledOrderEnabled: false }
                })
            }
            return Promise.resolve<string>(response.data.message || 'Scheduled order disabled')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to update'))
    })
}

export const pauseOrders = (durationMinutes: 30 | 60 | 120) => async (dispatch: Dispatch, getState: GetState) => {
    const authAdmin = getState().authAdmin
    const config = getApiRequestConfig(authAdmin?.['auth-token'])

    return api.put<Response<{ ordersPausedUntil: string }>>('shop/pause-orders', { durationMinutes }, config).then(response => {
        if (response.status === 200) {
            const ordersPausedUntil = response.data.data?.ordersPausedUntil ?? null
            dispatch<UpdateShopOrdersPauseAction>({
                type: AuthAdminActionTypes.UPDATE_SHOP_ORDERS_PAUSE,
                data: { ordersPausedUntil }
            })
            if (authAdmin?.shop?.id) {
                dispatch({
                    type: ShopActionTypes.UPDATE_SHOP_ORDERS_PAUSE,
                    shopId: authAdmin.shop.id,
                    data: { ordersPausedUntil }
                })
            }
            return Promise.resolve<string>(response.data.message || 'Orders paused')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to pause orders'))
    })
}

export const resumeOrders = () => async (dispatch: Dispatch, getState: GetState) => {
    const authAdmin = getState().authAdmin
    const config = getApiRequestConfig(authAdmin?.['auth-token'])

    return api.put<Response<{ ordersPausedUntil: null }>>('shop/resume-orders', {}, config).then(response => {
        if (response.status === 200) {
            dispatch<UpdateShopOrdersPauseAction>({
                type: AuthAdminActionTypes.UPDATE_SHOP_ORDERS_PAUSE,
                data: { ordersPausedUntil: null }
            })
            if (authAdmin?.shop?.id) {
                dispatch({
                    type: ShopActionTypes.UPDATE_SHOP_ORDERS_PAUSE,
                    shopId: authAdmin.shop.id,
                    data: { ordersPausedUntil: null }
                })
            }
            return Promise.resolve<string>(response.data.message || 'Orders resumed')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to resume orders'))
    })
}

interface UpdateDeliveryChargeAndDistance {
    deliveryChargeForEveryKmAboveGivenDistance: number
    deliveryChargeUpToGivenDistance: number
    lateNightDeliveryChargeForEveryKm: number
    lateNightDeliveryFrom: string
    lateNightDeliveryTo: string
    deliveryDistanceFrom: number
    orderValueForFreeDeliveryUpToGivenDistance: number
    deliveryDistanceTo: number
    orderValueForFreeDeliveryAboveGivenDistance: number
}

interface UpdateDeliveryChargeAndDistanceResponse {
    charge: DeliveryCharge
    distance: DeliveryDistance
    lateNightDelivery: LateNightDelivery
    orderValueForFreeDelivery: OrderValueForFreeDelivery
}

export const updateDeliveryChargeAndDistance = (input: UpdateDeliveryChargeAndDistance) => async (dispatch: Dispatch, getState: GetState) => {
    const authAdmin = getState().authAdmin
    const config = getApiRequestConfig(authAdmin?.['auth-token'])

    return api.put<Response<UpdateDeliveryChargeAndDistanceResponse>>('shop/delivery-charge-and-distance', input, config).then(response => {
        if (response.status === 200) {
            dispatch<UpdateDeliveryChargeAndDistanceAction>({
                type: AuthAdminActionTypes.UPDATE_DELIVERY_CHARGE_AND_DISTANCE,
                data: response.data.data
            })
            return Promise.resolve<string>(response.data.message || 'Delivery charge and distance updated')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to update'))
    })
}

const setShippingDeliveryEnabled = (enabled: boolean) => async (dispatch: Dispatch, getState: GetState) => {
    const authAdmin = getState().authAdmin
    const config = getApiRequestConfig(authAdmin?.['auth-token'])

    return api.put(`shop/shipping-delivery/${enabled ? 'enable' : 'disable'}`, {}, config).then(response => {
        if (response.status === 200) {
            dispatch<UpdateShippingDeliveryAction>({
                type: AuthAdminActionTypes.UPDATE_SHIPPING_DELIVERY,
                data: { enabled }
            })
            return Promise.resolve<string>(response.data.message || (enabled ? 'Shipping delivery enabled' : 'Shipping delivery disabled'))
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to update'))
    })
}

export const enableShippingDelivery = () => setShippingDeliveryEnabled(true)
export const disableShippingDelivery = () => setShippingDeliveryEnabled(false)

export const updateShippingDeliveryChargeAndDistance = (input: UpdateDeliveryChargeAndDistance) => async (dispatch: Dispatch, getState: GetState) => {
    const authAdmin = getState().authAdmin
    const config = getApiRequestConfig(authAdmin?.['auth-token'])

    return api.put<Response<UpdateDeliveryChargeAndDistanceResponse>>('shop/shipping-delivery-charge-and-distance', input, config).then(response => {
        if (response.status === 200) {
            dispatch<UpdateShippingDeliveryAction>({
                type: AuthAdminActionTypes.UPDATE_SHIPPING_DELIVERY,
                data: response.data.data
            })
            return Promise.resolve<string>(response.data.message || 'Shipping delivery charge and distance updated')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to update'))
    })
}

export const updateDeliveryAPI = (input: DeliveryAPI) => async (dispatch: Dispatch, getState: GetState) => {
    const authAdmin = getState().authAdmin
    const config = getApiRequestConfig(authAdmin?.['auth-token'])

    return api.put<Response<DeliveryAPI>>('shop/delivery-api', input, config).then(response => {
        if (response.status === 200) {
            dispatch<UpdateDeliveryAPIAction>({
                type: AuthAdminActionTypes.UPDATE_DELIVERY_API,
                data: response.data.data
            })
            if (authAdmin?.shop?.id) {
                dispatch({
                    type: ShopActionTypes.UPDATE_SHOP_DELIVERY_API,
                    shopId: authAdmin.shop.id,
                    data: response.data.data
                })
            }
            return Promise.resolve<string>(response.data.message || 'Delivery api updated')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to update'))
    })
}

export const updatePaytrail = (input: Paytrail) => async (dispatch: Dispatch, getState: GetState) => {
    const authAdmin = getState().authAdmin
    const config = getApiRequestConfig(authAdmin?.['auth-token'])

    return api.put<Response<Paytrail>>('shop/paytrail', input, config).then(response => {
        if (response.status === 200) {
            dispatch<UpdatePaytrailAction>({
                type: AuthAdminActionTypes.UPDATE_PAYTRAIL,
                data: response.data.data
            })
            return Promise.resolve<string>(response.data.message || 'Paytrail updated')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to update'))
    })
}

export const updateProcessTimes = (input: number[]) => async (dispatch: Dispatch, getState: GetState) => {
    const authAdmin = getState().authAdmin
    const config = getApiRequestConfig(authAdmin?.['auth-token'])

    return api.put<Response<number[]>>('shop/process-time', input, config).then(response => {
        if (response.status === 200) {
            dispatch<UpdateProcessTimesAction>({
                type: AuthAdminActionTypes.UPDATE_PROCESS_TIMES,
                data: response.data.data
            })
            return Promise.resolve<string>(response.data.message || 'Process time updated')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to update'))
    })
}

interface UpdateShopTheme {
    bannerImage: string | null
    primaryColor: string | null
    secondaryColor: string | null
    slides: {
        image: string
        productUrl: string
        description?: string
        title?: string
    }[]
}

export const updateShopTheme = (input: UpdateShopTheme) => async (dispatch: Dispatch, getState: GetState) => {
    const authAdmin = getState().authAdmin
    const config = getApiRequestConfig(authAdmin?.['auth-token'])

    return api.put<Response<Theme>>('shop/theme', input, config).then(response => {
        if (response.status === 200) {
            dispatch<UpdateShopThemeAction>({
                type: AuthAdminActionTypes.UPDATE_SHOP_THEME,
                data: response.data.data
            })
            return Promise.resolve<string>(response.data.message || 'Shop theme updated')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to update'))
    })
}

interface UpdatePickups {
    addressLine1?: string
    addressLine2?: string
    state?: string
    city?: string
    pincode?: string
    phoneNumber?: string
    landmark?: string
}

export const UpdatePickup = (input: UpdatePickups[]) => async (dispatch: Dispatch, getState: GetState) => {
    const authAdmin = getState().authAdmin
    const config = getApiRequestConfig(authAdmin?.['auth-token'])

    return api.put<Response<Pickup>>('shop/pickup', { data: input }, config).then(response => {
        if (response.status === 200) {
            dispatch<UpdatePickupAction>({
                type: AuthAdminActionTypes.UPDATE_PICKUP,
                data: response.data.data
            })
            return Promise.resolve<string>(response.data.message || 'Pickup point updated')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to update'))
    })
}

interface Price {
    worsePrice: string | null
    bestPrice: string | null
}

interface UpdateShopPreviewTheme {
    animation: string | null
    fontStyle: string | null
    backgroundColor: string | null
    color: string | null
    priceColor: Price
}

export const updateShopPreviewTheme = (input: UpdateShopPreviewTheme) => async (dispatch: Dispatch, getState: GetState) => {
    const authAdmin = getState().authAdmin
    const config = getApiRequestConfig(authAdmin?.['auth-token'])

    return api.put<Response<UpdateShopPreviewTheme>>('shop/preview-theme', input, config).then(response => {
        if (response.status === 200) {
            dispatch<UpdateShopPreviewThemeAction>({
                type: AuthAdminActionTypes.UPDATE_SHOP_PREVIEW_THEME,
                data: response.data.data
            })
            return Promise.resolve<string>(response.data.message || 'Shop preview theme updated')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to update'))
    })
}


export const updateVatId = (input: string | null) => async (dispatch: Dispatch, getState: GetState) => {
    const authAdmin = getState().authAdmin
    const config = getApiRequestConfig(authAdmin?.['auth-token'])

    return api.put<Response<string | null>>('shop/vat-id', { vatId: input }, config).then(response => {
        if (response.status === 200) {
            dispatch<UpdateShopVatIdAction>({
                type: AuthAdminActionTypes.UPDATE_SHOP_VAT_ID,
                data: response.data.data
            })
            return Promise.resolve<string>(response.data.message || 'Shop vat id updated')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to update'))
    })
}

export const updateShopAnnouncement = (input: string | null) => async (dispatch: Dispatch, getState: GetState) => {
    const authAdmin = getState().authAdmin
    const config = getApiRequestConfig(authAdmin?.['auth-token'])

    return api.put<Response<string | null>>('shop/announcement', { shopAnnouncement: input }, config).then(response => {
        if (response.status === 200) {
            dispatch<UpdateShopAnnouncementAction>({
                type: AuthAdminActionTypes.UPDATE_SHOP_ANNOUNCEMENT,
                data: response.data.data
            })
            return Promise.resolve<string>(response.data.message || 'Shop announcement updated')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to update'))
    })
}

export interface LoginAdmin {
    username: string
    password: string
}

export const loginAdmin = (input: LoginAdmin) => async (dispatch: Dispatch) => {
    return api.post<Response<AuthAdmin>>('admin/login', input).then(response => {
        if (response.status === 200) {
            dispatch<LoginAdminAction>({
                type: AuthAdminActionTypes.LOGIN_ADMIN,
                data: response.data.data
            })
            return Promise.resolve<string>(response.data.message || 'Logged in')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to login'))
    })
}

export interface LoginForSuperAdmin {
    username: string
}

export const loginForSuperAdmin = (input: LoginForSuperAdmin) => async (dispatch: Dispatch, getState: GetState) => {
    const authSuperAdmin = getState().authSuperAdmin
    const config = getApiRequestConfig(authSuperAdmin?.['auth-token'])

    return api.post<Response<AuthAdmin>>('superAdmin/login', input, config).then(response => {
        if (response.status === 200) {
            dispatch<LoginAdminAction>({
                type: AuthAdminActionTypes.LOGIN_ADMIN,
                data: response.data.data
            })
            return Promise.resolve<string>(response.data.message || 'Super Admin Logged in')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to super admin login'))
    })
}

export const logoutAdmin = (): LogoutAdminAction => {
    return {
        type: AuthAdminActionTypes.LOGOUT_ADMIN
    }
}